/**
 * ServicesOverview - Home page services preview with 4 categories
 * Redesigned as a high-contrast premium grid with minimal elements
 */
import React from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { FaShip, FaTruckMoving, FaWarehouse, FaLeaf, FaArrowRight } from 'react-icons/fa';
import { StaggerContainer, StaggerItem } from '../ui/AnimatedSection';
import SectionHeader from '../ui/SectionHeader';
import StaffingService from './StaffingService';

const services = [
  {
    num: '01',
    icon: FaShip,
    title: 'Shipping & Marine Services',
    desc: 'Comprehensive shipping agency, vessel husbanding, container, bulk, tanker ship handling, and ODC transportation.',
    items: ['Shipping Agency Operations', 'Vessel Husbanding & Port Stays', 'Container, Bulk & Tanker Handling', 'ODC Transportation Services'],
    hash: '#shipping-marine'
  },
  {
    num: '02',
    icon: FaTruckMoving,
    title: 'Logistics & Project Cargo',
    desc: 'Expert handling of project cargo, heavy lifts, Car Carrier & RO-RO logistics, windmill components, and ship chartering & brokering.',
    items: ['Heavy Lift & OOG Solutions', 'Windmill Renewable Logistics', 'Car Carrier & RO-RO Logistics', 'Global Ship Chartering & Brokering'],
    hash: '#logistics-project'
  },
  {
    num: '03',
    icon: FaWarehouse,
    title: 'Integrated Logistics',
    desc: 'End-to-end logistics: CFS, warehousing, transportation, freight forwarding, customs and supply chain management.',
    items: ['CFS Consolidation & Stuffing', 'Secure Warehousing & Inventory', 'Multimodal Road & Rail Transport', 'Statutory Customs Clearance'],
    hash: '#integrated-logistics'
  },
  {
    num: '04',
    icon: FaLeaf,
    title: 'Green Energy Solutions',
    desc: 'Shore power, VFD, port electrification and sustainable infrastructure for the future of maritime.',
    items: ['Ashore Marine Power (Cold Ironing)', 'Variable Frequency Drive Systems', 'High-Voltage Port Electrification', 'Sustainable Net Zero Infrastructure'],
    hash: '#green-energy'
  }
];

const ServicesOverview = () => (
  <section style={{
    padding: '4.5rem 0',
    background: 'var(--bg-dark)',
    position: 'relative',
    overflow: 'hidden'
  }}>
    {/* Subtle architectural vertical lines */}
    <div style={{
      position: 'absolute', top: 0, bottom: 0, left: '25%', width: '1px',
      background: 'rgba(255,255,255,0.02)', pointerEvents: 'none'
    }} />
    <div style={{
      position: 'absolute', top: 0, bottom: 0, left: '75%', width: '1px',
      background: 'rgba(255,255,255,0.02)', pointerEvents: 'none'
    }} />

    <div className="container-xxl px-4 px-lg-5" style={{ position: 'relative', zIndex: 1 }}>
      
      <SectionHeader
        tag="Services Portfolio"
        title="Key Operational Divisions"
        subtitle="Bridging gaps and establishing seamless trade lanes with a specialized suite of maritime and logistical services."
        light
      />

      <StaggerContainer className="row g-4 mt-2" staggerDelay={0.08}>
        {services.map((s, i) => {
          const IconComp = s.icon;
          const isGreen = s.hash === '#green-energy';
          return (
            <StaggerItem key={i} className="col-lg-6 col-xl-3">
              <motion.div
                whileHover={{ y: -6, borderColor: isGreen ? '#10B981' : 'var(--orange)' }}
                transition={{ duration: 0.3, ease: 'easeOut' }}
                style={{
                  background: 'var(--bg-card-dark)',
                  borderRadius: 4,
                  padding: '2.5rem 2rem',
                  height: '100%',
                  border: '1px solid rgba(255,255,255,0.06)',
                  position: 'relative',
                  display: 'flex',
                  flexDirection: 'column',
                  justifyContent: 'space-between',
                  transition: 'border-color 0.25s ease'
                }}
                className="service-overview-card"
              >
                <div>
                  {/* Number & Icon Row */}
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1.8rem' }}>
                    <span style={{
                      fontFamily: 'var(--font-heading)',
                      fontWeight: 700,
                      fontSize: '0.85rem',
                      color: isGreen ? '#10B981' : 'var(--orange)',
                      letterSpacing: '1px'
                    }}>{s.num}</span>
                    <IconComp size={18} color="rgba(255, 255, 255, 0.45)" />
                  </div>

                  <h4 style={{
                    fontFamily: 'var(--font-heading)',
                    fontWeight: 700,
                    color: '#fff',
                    fontSize: '1.05rem',
                    marginBottom: '1rem',
                    letterSpacing: '-0.01em'
                  }}>
                    {s.title}
                  </h4>
                  
                  <p style={{
                    color: 'rgba(255,255,255,0.5)',
                    fontSize: '0.82rem',
                    lineHeight: 1.7,
                    marginBottom: '1.8rem'
                  }}>
                    {s.desc}
                  </p>

                  {/* Division services checklist */}
                  <ul style={{ padding: 0, margin: '0 0 2rem' }}>
                    {s.items.map((item, j) => (
                      <li key={j} style={{
                        display: 'flex',
                        alignItems: 'baseline',
                        gap: '0.5rem',
                        fontSize: '0.78rem',
                        color: 'rgba(255,255,255,0.4)',
                        marginBottom: '0.4rem',
                        lineHeight: 1.4
                      }}>
                        <span style={{ color: isGreen ? '#10B981' : 'var(--orange)', fontSize: '0.55rem' }}>—</span>
                        {item}
                      </li>
                    ))}
                  </ul>
                </div>

                <Link to={`/services${s.hash}`} style={{
                  display: 'inline-flex',
                  alignItems: 'center',
                  gap: '0.45rem',
                  color: isGreen ? '#10B981' : 'var(--orange)',
                  fontSize: '0.75rem',
                  fontWeight: 700,
                  textDecoration: 'none',
                  fontFamily: 'var(--font-heading)',
                  letterSpacing: '1px',
                  textTransform: 'uppercase'
                }} className="service-card-link">
                  Explore Division <FaArrowRight size={8} />
                </Link>
              </motion.div>
            </StaggerItem>
          );
        })}
      </StaggerContainer>

      <StaffingService preview />

      {/* Corporate Action */}
      <div className="text-center mt-5 pt-3">
        <motion.div whileHover={{ y: -1 }} whileTap={{ scale: 0.98 }} style={{ display: 'inline-block' }}>
          <Link to="/services" style={{
            display: 'inline-flex',
            alignItems: 'center',
            gap: '0.65rem',
            background: 'var(--orange)',
            color: 'var(--navy)',
            fontFamily: 'var(--font-heading)',
            fontWeight: 700,
            fontSize: '0.8rem',
            padding: '1rem 2.5rem',
            borderRadius: 2,
            textDecoration: 'none',
            textTransform: 'uppercase',
            letterSpacing: '1.5px',
            boxShadow: 'none',
            transition: 'all 0.25s ease'
          }} className="btn-all-services">
            View All Services <FaArrowRight size={10} />
          </Link>
        </motion.div>
      </div>
    </div>

    <style>{`
      .service-overview-card:hover {
        border-color: var(--orange) !important;
      }
      .btn-all-services:hover {
        background: var(--orange-light) !important;
      }
      .service-card-link {
        position: relative;
      }
      .service-card-link::after {
        content: '';
        position: absolute;
        bottom: -2px;
        left: 0;
        width: 0;
        height: 1px;
        background: currentColor;
        transition: width 0.25s ease;
      }
      .service-overview-card:hover .service-card-link::after {
        width: 100%;
      }
    `}</style>
  </section>
);

export default ServicesOverview;
